import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { IncidentSummary } from '../components/IncidentBanner';
import { usePageMeta } from '../hooks/usePageMeta';
import { IncidentStatusChip, INCIDENT_STATUS_LABELS } from './IncidentsPage';

interface IncidentItem {
  id: number;
  source_table: string;
  source_key: string;
  source: string;
  title: string | null;
  url: string | null;
  item_date: string | null;
  match_reason: string | null;
  flags: string[];
}

interface IncidentEvent {
  id: number;
  type: string;
  from_value: string | null;
  to_value: string;
  detail: string | null;
  source_ref: string;
  created_at: string;
}

interface IncidentDetail {
  ok: boolean;
  incident: IncidentSummary;
  items: IncidentItem[];
  events: IncidentEvent[];
}

async function fetchIncident(key: string, token: string | null): Promise<IncidentDetail> {
  const res = await fetch(`${API_BASE_URL}/api/incidents/${encodeURIComponent(key)}`, {
    cache: 'no-store',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(res.status === 404 ? 'Incident not found' : await res.text());
  return res.json() as Promise<IncidentDetail>;
}

const SOURCE_LABELS: Record<string, string> = {
  rasff: 'RASFF',
  favv: 'Belgium FAVV',
  favv_news: 'Belgium FAVV',
  nvwa: 'Netherlands NVWA',
  rappelconso: 'France RappelConso',
  lebensmittelwarnung: 'Germany LMW',
  fsai: 'Ireland FSAI',
  aesan: 'Spain AESAN',
  fsa_uk: 'UK FSA',
  openfda: 'US FDA',
  fsis: 'USDA FSIS',
  cfia: 'CFIA',
  fsanz: 'FSANZ',
  efsa: 'EFSA',
  sciensano: 'Sciensano',
  ecdc: 'ECDC',
  fda_core: 'US FDA outbreaks',
  ukhsa: 'UKHSA',
  spf: 'Santé publique France',
};

function sourceLabel(source: string): string {
  if (source.startsWith('media_')) return source === 'media_fsn' ? 'Food Safety News' : 'News';
  return SOURCE_LABELS[source] || source;
}

// The judge's evidence kinds (data_ingestion/incidents/crisis_judge.py).
const MARKER_LABELS: Record<string, string> = {
  illness: 'Confirmed cases',
  deaths: 'Deaths',
  hospitalisations: 'Hospitalisations',
  facility_closure: 'Facility closed or blocked',
  all_products_recall: 'Entire range recalled',
  farms_blocked: 'Farms blocked',
  trade_ban: 'Trade ban or emergency measure',
  record_language: 'Described as a record',
  multi_country_root: 'One root cause, many countries',
  vulnerable_group: 'Infants or vulnerable group',
  media_coverage: 'Media coverage',
};

interface CrisisEvidence {
  marker: string;
  quote: string;
  source: string;
  url: string | null;
  date: string | null;
}

function EvidenceList({ evidence }: { evidence: CrisisEvidence[] }) {
  if (!evidence.length) return null;
  return (
    <div className="incident-evidence">
      <h2>Why this is a crisis</h2>
      <p className="incident-evidence__intro">
        Each line is quoted word for word from the source shown.
      </p>
      <ul>
        {evidence.map((e, i) => (
          <li key={i} className="incident-evidence__item">
            <span className="incident-evidence__marker">{MARKER_LABELS[e.marker] || e.marker}</span>
            <q className="incident-evidence__quote">{e.quote}</q>
            <span className="incident-evidence__source">
              {e.url
                ? <a href={e.url} target="_blank" rel="noreferrer">{sourceLabel(e.source)}</a>
                : sourceLabel(e.source)}
              {e.date ? `, ${formatDate(e.date)}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  opened: 'Incident opened',
  item_linked: 'Source item linked',
  item_unlinked: 'Source item unlinked',
  tier_changed: 'Status change',
  case_count_updated: 'Case count updated',
  flag_set: 'Signal confirmed',
  adjudicated: 'Adjudication',
  merged_into: 'Merged away',
  merged_from: 'Absorbed incident',
  note: 'Note',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function eventText(e: IncidentEvent): string {
  if (e.type === 'tier_changed') {
    return `${e.from_value || 'new'} → ${e.to_value}`;
  }
  if (e.type === 'case_count_updated') {
    return `confirmed cases ${e.from_value || '0'} → ${e.to_value}`;
  }
  if (e.type === 'flag_set') {
    return e.to_value === 'facility_closure'
      ? 'Facility closed / blocked by the authority'
      : e.to_value === 'record_language'
        ? 'Authority calls this a record event'
        : e.to_value;
  }
  if (e.type === 'merged_into' || e.type === 'merged_from') {
    return e.detail || e.to_value;
  }
  return e.detail || e.to_value || '';
}

/** Editorial controls, only rendered for the authenticated editor. */
function AdminPanel({ incident, onChanged }: { incident: IncidentSummary; onChanged: () => void }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(incident.status);
  const [override, setOverride] = useState(incident.manual_override ?? false);
  const [mediaDate, setMediaDate] = useState(incident.media_reported_date ?? '');
  const [mergeTarget, setMergeTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function save() {
    setBusy(true); setMsg('');
    try {
      const res = await authFetch(`${API_BASE_URL}/api/admin/incidents/${encodeURIComponent(incident.key)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          manual_override: override,
          media_reported_date: mediaDate || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'save failed');
      setMsg('Saved.');
      onChanged();
    } catch (e: any) {
      setMsg(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function merge() {
    const target = mergeTarget.trim();
    if (!target) return;
    if (!window.confirm(`Merge this incident into "${target}"? Items move over; this dossier closes and locks.`)) return;
    setBusy(true); setMsg('');
    try {
      const res = await authFetch(`${API_BASE_URL}/api/admin/incidents/${encodeURIComponent(incident.key)}/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ into: target }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'merge failed');
      navigate(`/incidents/${target}`);
    } catch (e: any) {
      setMsg(e.message || 'Merge failed');
      setBusy(false);
    }
  }

  return (
    <div className="incident-admin">
      <span className="incident-admin__label">Editor</span>
      <select value={status} onChange={e => setStatus(e.target.value)} disabled={busy}>
        {Object.keys(INCIDENT_STATUS_LABELS).map(s => (
          <option key={s} value={s}>{INCIDENT_STATUS_LABELS[s]}</option>
        ))}
      </select>
      <label className="incident-admin__check">
        <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} disabled={busy} />
        lock (manual override)
      </label>
      <label className="incident-admin__check" title="When national media first reported it — feeds the detection ledger">
        media:
        <input type="date" value={mediaDate} onChange={e => setMediaDate(e.target.value)}
               disabled={busy} className="incident-admin__input" style={{ minWidth: 0 }} />
      </label>
      <button className="incident-admin__btn" onClick={save} disabled={busy}>Save</button>
      <span className="incident-admin__sep" />
      <input
        placeholder="merge into incident key…"
        value={mergeTarget}
        onChange={e => setMergeTarget(e.target.value)}
        disabled={busy}
        className="incident-admin__input"
      />
      <button className="incident-admin__btn incident-admin__btn--danger" onClick={merge}
              disabled={busy || !mergeTarget.trim()}>
        Merge
      </button>
      {msg && <span className="incident-admin__msg">{msg}</span>}
    </div>
  );
}

export default function IncidentDetailPage() {
  const { key = '' } = useParams<{ key: string }>();
  const { isAuthenticated, token } = useAuth();
  const { data, isLoading, isError, error, refetch } = useQuery<IncidentDetail, Error>(
    ['incident', key, isAuthenticated],
    () => fetchIncident(key, token),
    { retry: 1, enabled: Boolean(key) },
  );

  // Clusters reach this page only with an editor login; never offer them for indexing.
  const metaIncident = data?.incident;
  usePageMeta({
    title: metaIncident?.title ?? 'Food safety incident',
    // The narrative is markdown; its opening lines are the fallback lede.
    description: metaIncident?.summary ?? metaIncident?.narrative?.replace(/[#*_>`]/g, '') ?? null,
    noindex: isError || (!!metaIncident && metaIncident.kind !== 'crisis'),
  });

  async function unlinkItem(itemId: number) {
    if (!window.confirm('Unlink this source item from the incident?')) return;
    const res = await authFetch(
      `${API_BASE_URL}/api/admin/incidents/${encodeURIComponent(key)}/items/${itemId}`,
      { method: 'DELETE' },
    );
    if (res.ok) refetch();
  }

  if (isLoading) return <section className="incidents-page"><LoadingSkeleton rows={6} /></section>;
  if (isError || !data) {
    return (
      <section className="incidents-page">
        <h1>Incident</h1>
        <p className="admin-error">{error?.message || 'Could not load this incident.'}</p>
        <Link to="/incidents" className="incident-detail__back">← All incidents</Link>
      </section>
    );
  }

  const inc = data.incident;
  const stats: Array<[string, string]> = [];
  if (inc.case_count != null) stats.push(['Confirmed cases', String(inc.case_count)]);
  if (inc.hospitalizations != null) stats.push(['Hospitalised', String(inc.hospitalizations)]);
  if (inc.deaths != null) stats.push(['Deaths', String(inc.deaths)]);
  stats.push(['Countries', inc.countries.length ? String(inc.countries.length) : '—']);
  stats.push(['First signal', formatDate(inc.first_signal_date)]);
  stats.push(['Latest item', formatDate(inc.last_item_date)]);

  return (
    <section className="incidents-page incident-detail">
      <Link to="/incidents" className="incident-detail__back">← All incidents</Link>

      <div className="incident-detail__head">
        <IncidentStatusChip status={inc.status} />
        <h1>{inc.title}</h1>
        {/* The narrative opens with its own lede; the short summary only
            stands in while no narrative has been written yet. */}
        {!inc.narrative && inc.summary && (
          <p className="incident-detail__summary">{inc.summary}</p>
        )}
        <div className="incident-detail__tags">
          {inc.pathogen && (
            <span className="incident-card__tag">
              {[inc.pathogen, inc.serotype].filter(Boolean).join(' ')}
            </span>
          )}
          {inc.vehicle && <span className="incident-card__tag">{inc.vehicle}</span>}
          {inc.producer && <span className="incident-card__tag">{inc.producer}</span>}
          {inc.origin_country && <span className="incident-card__tag">origin: {inc.origin_country}</span>}
          {inc.approval_codes.map(c => (
            <span key={c} className="incident-card__tag incident-card__tag--code">{c}</span>
          ))}
        </div>
      </div>

      {isAuthenticated && <AdminPanel incident={inc} onChanged={() => refetch()} />}

      {inc.narrative && (
        <article className="incident-article">
          {inc.narrative.split(/\n{2,}/).map((para, i) => (
            <p key={i}>{para.trim()}</p>
          ))}
        </article>
      )}

      <EvidenceList evidence={(inc.crisis_evidence || []) as CrisisEvidence[]} />

      <div className="incident-detail__stats">
        {stats.map(([label, value]) => (
          <div key={label} className="incident-stat">
            <div className="incident-stat__value">{value}</div>
            <div className="incident-stat__label">{label}</div>
          </div>
        ))}
      </div>

      {inc.countries.length > 1 && (
        <p className="incident-detail__countries">
          <strong>Distribution / recalls:</strong> {inc.countries.join(', ')}
        </p>
      )}
      {inc.case_count_source && (
        <p className="incident-detail__source-note">
          Case count per <a href={inc.case_count_source} target="_blank" rel="noreferrer">authority statement</a>.
        </p>
      )}

      <div className="incident-detail__cols">
        <div>
          <h2>Timeline</h2>
          <ol className="incident-timeline">
            {data.events.map(e => (
              <li key={e.id} className={`incident-timeline__item incident-timeline__item--${e.type}`}>
                <span className="incident-timeline__date">{formatDate(e.created_at)}</span>
                <span className="incident-timeline__type">{EVENT_LABELS[e.type] || e.type}</span>
                <span className="incident-timeline__text">{eventText(e)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <h2>Linked source items <span className="incident-detail__count">({data.items.length})</span></h2>
          <ul className="incident-items">
            {data.items.map(it => (
              <li key={it.id} className="incident-items__row">
                <span className="incident-items__date">{formatDate(it.item_date)}</span>
                <span className="incident-items__source">{sourceLabel(it.source)}</span>
                {it.url ? (
                  <a href={it.url} target="_blank" rel="noreferrer" className="incident-items__title">
                    {it.title || it.source_key}
                  </a>
                ) : (
                  <span className="incident-items__title">{it.title || it.source_key}</span>
                )}
                {isAuthenticated && (
                  <button className="incident-items__unlink" title="Unlink from incident"
                          onClick={() => unlinkItem(it.id)}>
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
